import { EAlertTypes } from '../../models/alerts/alert-configuration';
import VesselTypes from '../../models/vessels/vessel-types.model';
import { Token, UserFeatureFlags } from '../../user/user.slice';
import { getReleaseToggles, ReleaseToggles } from './release-toggles';

export type Privileges = {
  canAccessAlerts: Record<EAlertTypes, boolean>;
  canAccessVesselTypes: Record<VesselTypes, boolean>;
  canAccessIncidents: boolean;
  canAccessMyFleetV1: {
    ais: boolean;
    ri: boolean;
  };
  canAccessPorts: {
    wpi: boolean;
    land: boolean;
    coastal: boolean;
    cruise: boolean;
  };
  canAccessRoutes: boolean;
  canAccessHistory: boolean;
  canAccessBoundaries: boolean;
  canAccessDrawings: boolean;
  canAccessMaps: {
    openSeaMaps: boolean;
    bathymetry: boolean;
  };
  canAccessSanctions: boolean;
  canAccessRiMaritimeAreas: boolean;
  canAccessGraphs: boolean;
  canAccessHistoricVessels: boolean;
  canAccessSharedDrawings: boolean;
  canMultiSelect: boolean;
  canAccessRiBoundaries: boolean;
  canAccessHelp: boolean;
  canAccessDidYouKnow: boolean;
  canAccessIncidentsDateRangeIndicator: boolean;
  canAccessDatumRings: boolean;
  canDrawPoints: boolean;
  canAccessFathom: boolean;
  canAccessMapLayerHandler: boolean;
  canAccessPermissionToggles: boolean;
  canEditCompanySeats: boolean;
  canAccessMeasuringTool: boolean;
  canAccessRangeRings: boolean;
  canAccessNewsTicker: boolean;
  canAccessRICitiesCountriesData: boolean;
  canAccessSearchV2: boolean;
  canAccessDrawingColours: boolean;
  canAccessVesselListColours: boolean;
  canAccessLastLogin: boolean;
};

export const NO_PRIVILEGES: Privileges = {
  canAccessAlerts: {
    AIS_OFF: false,
    AIS_ON: false,
    ENTER_PORT: false,
    ENTER_ROI: false,
    EXIT_ROI: false,
    INCIDENTS: false,
    INCIDENTS_ROUTE: false,
    DEP_PORT: false,
    STS: false,
    STATIC_CHANGE: false,
  },
  canAccessVesselTypes: {
    DRY_BULK: false,
    GENERAL_CARGO: false,
    CONTAINER: false,
    REEFER: false,
    ROLL_ON_ROLL_OFF: false,
    OFFSHORE: false,
    HIGH_SPEED_CRAFT: false,
    CAR_CARRIER: false,
    VEHICLE_PASSENGER: false,
    PLEASURE_CRAFT: false,
    FISHING: false,
    GENERAL_TANKER: false,
    GAS_TANKER: false,
    TANKER_PRODUCT: false,
    PASSENGER: false,
    OTHER_UNKNOWN: false,
    BUOY: false,
    NET: false,
  },
  canAccessIncidents: false,
  canAccessMyFleetV1: {
    ais: false,
    ri: false,
  },
  canAccessPorts: {
    wpi: false,
    land: false,
    coastal: false,
    cruise: false,
  },
  canAccessRoutes: false,
  canAccessHistory: false,
  canAccessBoundaries: false,
  canAccessDrawings: false,
  canAccessMaps: {
    openSeaMaps: false,
    bathymetry: false,
  },
  canAccessSanctions: false,
  canAccessRiMaritimeAreas: false,
  canAccessGraphs: false,
  canAccessHistoricVessels: false,
  canAccessSharedDrawings: false,
  canMultiSelect: false,
  canAccessRiBoundaries: false,
  canAccessHelp: false,
  canAccessDidYouKnow: false,
  canAccessIncidentsDateRangeIndicator: false,
  canAccessDatumRings: false,
  canDrawPoints: false,
  canAccessFathom: false,
  canAccessMapLayerHandler: false,
  canAccessPermissionToggles: false,
  canEditCompanySeats: false,
  canAccessMeasuringTool: false,
  canAccessRangeRings: false,
  canAccessNewsTicker: false,
  canAccessRICitiesCountriesData: false,
  canAccessSearchV2: false,
  canAccessDrawingColours: false,
  canAccessVesselListColours: false,
  canAccessLastLogin: false,
};

export const riskIntelligencePrivileges = (
  userFeatureFlags: UserFeatureFlags | null,
  releaseToggles: ReleaseToggles
): Privileges => ({
  ...NO_PRIVILEGES,
  canAccessAlerts: {
    ...NO_PRIVILEGES.canAccessAlerts,
    ENTER_ROI: userFeatureFlags?.fleet_license || false,
    INCIDENTS: userFeatureFlags?.fleet_license || false,
    INCIDENTS_ROUTE: userFeatureFlags?.voyager_intel_tool || false,
  },
  canAccessVesselTypes: {
    ...NO_PRIVILEGES.canAccessVesselTypes,
  },
  canAccessIncidents: true,
  canAccessMyFleetV1: {
    ais: userFeatureFlags?.fleet_license || false,
    ri: true,
  },
  canAccessPorts: {
    wpi:
      userFeatureFlags?.ports_license ||
      userFeatureFlags?.cruise_license ||
      false,
    land:
      userFeatureFlags?.ports_license ||
      userFeatureFlags?.cruise_license ||
      false,
    coastal:
      userFeatureFlags?.ports_license ||
      userFeatureFlags?.cruise_license ||
      false,
    cruise:
      (userFeatureFlags?.ports_license && userFeatureFlags?.cruise_license) ||
      false,
  },
  canAccessRoutes: userFeatureFlags?.voyager_intel_tool || false,
  canAccessHistory: userFeatureFlags?.ais_history || false,
  canAccessBoundaries: true,
  canAccessDrawings: true,
  canAccessMaps: {
    openSeaMaps: true,
    bathymetry: false,
  },
  canAccessSanctions: userFeatureFlags?.sanctions || false,
  canAccessRiMaritimeAreas: true,
  canAccessRiBoundaries: true,
  canAccessHelp: true,
  canAccessDidYouKnow: true,
  canAccessIncidentsDateRangeIndicator: true,
  canAccessDatumRings: true,
  canAccessFathom: true,
  canEditCompanySeats: false,
  canAccessNewsTicker: true,
  canAccessRICitiesCountriesData: true,
  canAccessDrawingColours: releaseToggles.drawingColourEnabled,
  canAccessVesselListColours: releaseToggles.vesselListColourEnabled,
  canAccessLastLogin: releaseToggles.lastLoginEnabled,
});

type CalculatePrivilegesArgs = {
  idToken: Token | null;
  userFeatureFlags: UserFeatureFlags | null;
};

export const calculatePrivileges = ({
  idToken,
  userFeatureFlags,
}: CalculatePrivilegesArgs) => {
  if (!idToken) return NO_PRIVILEGES;
  const releaseToggles = getReleaseToggles();

  return riskIntelligencePrivileges(userFeatureFlags, releaseToggles);
};
